import { modifyMutable, produce } from 'solid-js/store'
import type { ActionBindingEvent } from '../../event/InputRouter/ActionBindingEvent.type'
import { useIsEngineModeGameMemo } from '../../../rx/memo/useIsEngineModeGameMemo'
import { useIsEngineModeMapEditorMemo } from '../../../rx/memo/useIsEngineModeMapEditorMemo'
import { useIsEngineModeNotArmyPainterMemo } from '../../../rx/memo/useIsEngineModeNotArmyPainterMemo'
import { PERSPECTIVE_CAMERA_FAR_STEP } from '../../../rx/shared/device/userPreference/CameraFar'
import { PERSPECTIVE_CAMERA_FOV_STEP } from '../../../rx/shared/device/userPreference/CameraFov'
import { getDeviceUserPreference } from '../../../rx/shared/device/userPreference/deviceUserPreferencesSignal'
import { PIXELATION_SHADER_INTENSITY_VALUE_STEP } from '../../../rx/shared/device/userPreference/PixelationShaderIntensityValue'
import { updateCameraFar } from '../../../rx/shared/device/userPreference/updateCameraFar'
import { updateCameraFov } from '../../../rx/shared/device/userPreference/updateCameraFov'
import { updatePixelationShaderIntensityValue } from '../../../rx/shared/device/userPreference/updatePixelationShaderIntensityValue'
import useIsActionLogListModalOpenSignal from '../../../rx/shared/ModalMenu/useIsActionLogListModalOpenSignal'
import useIsPlayersListModalOpenSignal from '../../../rx/shared/ModalMenu/useIsPlayersListModalOpenSignal'
import { useDraftMoveSignal } from '../../../rx/signal/useDraftMoveSignal'
import { useSelectedPositionListSignal } from '../../../rx/signal/useSelectedPositionListSignal'
import { useSelectedPositionSignal } from '../../../rx/signal/useSelectedPositionSignal'
import { useSelectedToolSignal } from '../../../rx/signal/useSelectedToolSignal'
import useSelectionModeSignal from '../../../rx/signal/useSelectionModeSignal'
import { Engine } from '../../core/engine/Engine.type'
import resetEngineToDefaultSelectedTool from '../../core/engine/resetEngineToDefaultSelectedTool'
import { SelectionMode } from '../../core/engine/SelectionMode/SelectionMode.enum'
import calculateSelectedPositionEnts from '../../core/entity/calculateSelectedPositionEnts'
import canUnitAnnexEntity from '../../core/entity/canUnitAnnexEntity'
import centerPanCameraToPosition from '../../core/entity/EntityHeading/centerPanCameraToPosition'
import { CLOCKWISE, COUNTER_CLOCKWISE } from '../../core/entity/EntityHeading/consts'
import rotateCamera from '../../core/entity/EntityHeading/rotateCamera'
import { isEntityInfantry } from '../../core/entity/entityTypeMetaList.generated'
import getEntitiesAtPosition from '../../core/entity/getEntitiesAtPosition'
import findByXY from '../../core/findByXY'
import { SelectedToolId } from '../../core/map_editor/SelectedToolId.enum'
import { createMoveUnitAction } from '../../core/state/flux/action/Game/MoveUnitAction'
import dispatchClient from '../../core/state/flux/dispatchClient'
import handlePreviewRangedAttackOptions from '../../core/ui/action/PreviewRangedAttackOptions'
import handlePreviewUnloadPositionOptions from '../../core/ui/action/PreviewUnloadPositionOptions'
import handleRotateNextHome from '../../core/ui/action/RotateNextHome'
import handleRotateTileEntityHeading from '../../core/ui/action/RotateTileEntityHeading'
import handleRotateUnitSelection from '../../core/ui/action/RotateUnitSelection'
import handleSetCameraPolarAngleDefault from '../../core/ui/action/SetCameraPolarAngleDefault'
import handleSetCameraPolarAngleTopDown from '../../core/ui/action/SetCameraPolarAngleTopDown'
import handleUnitLoadIntoEntityAction from '../../core/ui/action/UnitLoadIntoEntityAction'
import handleUnitPickupEntityAction from '../../core/ui/action/UnitPickupEntityAction'
import { deepClone } from '../../deep_clone'
import { emitEvent, EventResult } from '../../event/event'
import { actionBindings } from '../../event/InputRouter/actionBindings'
import getInputActionListenerList from '../../event/InputRouter/getInputActionListenerList'
import { InputActionType } from '../../event/InputRouter/InputActionType'
import keyboardEventMatchesInputActionBinding from '../../event/InputRouter/keyboardEventMatchesInputActionBinding'
import { mountInputActionListener, mountInputActionListenerWhen } from '../../event/InputRouter/mountInputActionListener'
import firstIfOnlyOne from '../../ldsh/firstIfOnlyOne'
import onDocumentEvent from '../../onDocumentEvent'
import onWindowEvent from '../../onWindowEvent'
import { getEngineThreeControls } from '../../three/fn/getSetEngineThreeControls'
import { getEngineThreeMainCamera } from '../../three/fn/getSetEngineThreeMainCamera'

// TODO optional "grid" hotkeys preset.
// grid hotkeys put the hotkeys closer together?

const CORDOVA = false

export default function registerGamePlayKeyboardEvents(engine: Engine) {
  const [, setSelectedTool] = useSelectedToolSignal(engine)
  const [getSelectedPosition] = useSelectedPositionSignal(engine)
  const [, setSelectionMode] = useSelectionModeSignal(engine)
  const [getSelectedPositionList, setSelectedPositionList] = useSelectedPositionListSignal(engine)
  const [isPlayersListModalOpen, setIsPlayersListModalOpen] = useIsPlayersListModalOpenSignal()
  const [isActionLogListModalOpen, setIsActionLogListModalOpen] = useIsActionLogListModalOpenSignal()
  const [getDraftMove, setDraftMove] = useDraftMoveSignal(engine)
  const isEngineModeGame = useIsEngineModeGameMemo(engine)
  const isEngineModeMapEditor = useIsEngineModeMapEditorMemo(engine)
  const isEngineModeNotArmyPainter = useIsEngineModeNotArmyPainterMemo(engine)
  
  // const dirty = engine.cache[1]
  // const ab = createAlertBagStore()

  if (CORDOVA) {
    // have the android back button trigger actions associated with the ESC key
    // document.addEventListener("backbutton", yourCallbackFunction, false);
    onDocumentEvent('backbutton' as keyof DocumentEventMap, (event) => {
      const ell = getInputActionListenerList(InputActionType.Escape)
      if (ell.length > 0) {
        emitEvent(ell, event as unknown as ActionBindingEvent)
        return false
      }
    }, false)
  }

  mountInputActionListener(InputActionType.Escape, () => {
    modifyMutable(
      engine,
      produce((engine) => {
        resetEngineToDefaultSelectedTool(engine)
      })
    )
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.RotateClockwise, isEngineModeMapEditor, () => {
    handleRotateTileEntityHeading(engine, CLOCKWISE)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.RotateCounterClockwise, isEngineModeMapEditor, () => {
    handleRotateTileEntityHeading(engine, COUNTER_CLOCKWISE)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.CameraDefaultPolarAngle, isEngineModeNotArmyPainter, () => {
    // console.log('[Backspace] pressed')

    handleSetCameraPolarAngleDefault(engine)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.CameraTopDownPolarAngle, isEngineModeNotArmyPainter, () => {
    // console.log('[T] pressed')

    handleSetCameraPolarAngleTopDown(engine)
    return EventResult.Stop
  })

  mountInputActionListener(InputActionType.SelectInspectTool, () => {
    console.log('SelectInspectTool')
    setDraftMove(null)
    setSelectedTool(SelectedToolId.Inspect)
    calculateSelectedPositionEnts(engine)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectPaintTool, isEngineModeMapEditor, () => {
    setSelectedTool(SelectedToolId.Paint)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectEraseTool, isEngineModeMapEditor, () => {
    setSelectedTool(SelectedToolId.Erase)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectFileTool, isEngineModeMapEditor, () => {
    setSelectedTool(SelectedToolId.File)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectToolsTool, isEngineModeMapEditor, () => {
    setSelectedTool(SelectedToolId.Tools)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectPlayersTool, isEngineModeMapEditor, () => {
    setSelectedTool(SelectedToolId.Players)
    return EventResult.Stop
  })

  // Selection actions - only active in map editor mode and multi-select mode
  mountInputActionListenerWhen(InputActionType.SelectAll, isEngineModeMapEditor, () => {
    setSelectionMode(SelectionMode.MultiSelect)
    const allPositions = []
    for (let x = 0; x < engine.state.width; x++) {
      for (let y = 0; y < engine.state.height; y++) {
        allPositions.push({ x, y })
      }
    }
    setSelectedPositionList(allPositions)
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.SelectNone, isEngineModeMapEditor, () => {
    setSelectionMode(SelectionMode.MultiSelect)
    setSelectedPositionList([])
    return EventResult.Stop
  })

  mountInputActionListenerWhen(InputActionType.InvertSelection, isEngineModeMapEditor, () => {
    console.log('InvertSelection')
    setSelectionMode(SelectionMode.MultiSelect)
    const currentSelection = getSelectedPositionList()
    const invertedSelection = []
    
    // Generate all positions
    for (let x = 0; x < engine.state.width; x++) {
      for (let y = 0; y < engine.state.height; y++) {
        if (!findByXY(currentSelection, x, y)) {
          invertedSelection.push({ x, y })
        }
      }
    }
    
    setSelectedPositionList(invertedSelection)
    return EventResult.Stop
  })

  // toggle action log modal (works in both game and map editor modes)
  mountInputActionListener(InputActionType.ActionLogMenu, () => {
    setIsActionLogListModalOpen(!isActionLogListModalOpen())
    return EventResult.Stop
  })

  // center camera on selected unit
  mountInputActionListener(InputActionType.CenterToPosition, () => {
    const centerToPosition = getDraftMove()?.unit || getSelectedPosition()
    if (centerToPosition) {
      centerPanCameraToPosition(engine, centerToPosition)
      return EventResult.Stop
    }
  })

  // adjust camera FOV
  mountInputActionListener(InputActionType.CameraIncreaseFov, () => {
    const prefs = getDeviceUserPreference()

    const proposedNewFovValue = prefs.camera_fov + PERSPECTIVE_CAMERA_FOV_STEP
    updateCameraFov(proposedNewFovValue)
    return EventResult.Stop
  })

  mountInputActionListener(InputActionType.CameraDecreaseFov, () => {
    const prefs = getDeviceUserPreference()

    const proposedNewFovValue = prefs.camera_fov - PERSPECTIVE_CAMERA_FOV_STEP
    updateCameraFov(proposedNewFovValue)
    return EventResult.Stop
  })

  // adjust camera Far
  mountInputActionListener(InputActionType.CameraIncreaseFar, () => {
    const prefs = getDeviceUserPreference()

    const proposedNewFarValue = prefs.camera_fov + PERSPECTIVE_CAMERA_FAR_STEP
    updateCameraFar(proposedNewFarValue)
    return EventResult.Stop
  })

  mountInputActionListener(InputActionType.CameraDecreaseFov, () => {
    const prefs = getDeviceUserPreference()

    const proposedNewFovValue = prefs.camera_fov - PERSPECTIVE_CAMERA_FAR_STEP
    updateCameraFar(proposedNewFovValue)
    return EventResult.Stop
  })

  // adjust pixelation shader intensity value
  mountInputActionListener(InputActionType.CameraIncreasePixelation, () => {
    const prefs = getDeviceUserPreference()
    const proposedNewFovValue = prefs.psiv + PIXELATION_SHADER_INTENSITY_VALUE_STEP
    updatePixelationShaderIntensityValue(proposedNewFovValue)
    return EventResult.Stop
  })
  mountInputActionListener(InputActionType.CameraDecreasePixelation, () => {
    const prefs = getDeviceUserPreference()
    const proposedNewFovValue = prefs.psiv - PIXELATION_SHADER_INTENSITY_VALUE_STEP
    updatePixelationShaderIntensityValue(proposedNewFovValue)
    return EventResult.Stop
  })

  // adjust camera zoom
  mountInputActionListener(InputActionType.CameraIncreaseZoom, () => {
    throw new Error('Camera zoom increase not implemented')
    return EventResult.Stop
  })
  mountInputActionListener(InputActionType.CameraDecreaseZoom, () => {
    throw new Error('Camera zoom decrease not implemented')
    return EventResult.Stop
  })

  // rotate camera 45 degree
  mountInputActionListener(InputActionType.CameraRotateClockwise, () => {
    const camera = getEngineThreeMainCamera(engine)
    const controls = getEngineThreeControls(engine)
    if (camera && controls) {
      rotateCamera(camera, controls, 45)
    }
    return EventResult.Stop
  })
  mountInputActionListener(InputActionType.CameraRotateCounterClockwise, () => {
    const camera = getEngineThreeMainCamera(engine)
    const controls = getEngineThreeControls(engine)
    if (camera && controls) {
      rotateCamera(camera, controls, -45)
    }
    return EventResult.Stop
  })

  // select next/prev unit (cycle through player's units)
  mountInputActionListenerWhen(InputActionType.SelectPrevUnit, isEngineModeGame, () => {
    handleRotateUnitSelection(engine, false)
    return EventResult.Stop
  })
  mountInputActionListenerWhen(InputActionType.SelectNextUnit, isEngineModeGame, () => {
    handleRotateUnitSelection(engine, true)
    return EventResult.Stop
  })

  // Home for next Home Ent
  mountInputActionListenerWhen(InputActionType.RotateNextHome, isEngineModeGame, () => {
    handleRotateNextHome(engine, true)
    return EventResult.Stop
  })

  // command unit to wait
  mountInputActionListenerWhen(InputActionType.UnitWait, isEngineModeGame, () => {
    const draftMove = getDraftMove()
    if (draftMove) {
      dispatchClient(engine, createMoveUnitAction({ ...draftMove, wait: true }))
    }
    return EventResult.Stop
  })

  // commit draft move
  mountInputActionListenerWhen(InputActionType.CommitMove, isEngineModeGame, () => {
    const draftMove = getDraftMove()
    if (draftMove && !draftMove.destPositionPaths?.lastStep?.occupant) {
      dispatchClient(engine, createMoveUnitAction({ ...draftMove }))
    }
    return EventResult.Stop
  })

  // command unit to pickup entity as cargo
  mountInputActionListenerWhen(InputActionType.UnitPickup, isEngineModeGame, () => {
    return handleUnitPickupEntityAction(engine)
  })

  // toggle player list modal
  mountInputActionListenerWhen(InputActionType.PlayersList, isEngineModeGame, () => {
    setIsPlayersListModalOpen(!isPlayersListModalOpen())
    return EventResult.Stop
  })

  // command unit to load into entity
  mountInputActionListenerWhen(InputActionType.UnitLoad, isEngineModeGame, () => {
    handleUnitLoadIntoEntityAction(engine)
    return EventResult.Stop
  })

  // preview unload command options
  mountInputActionListenerWhen(InputActionType.UnitUnload, isEngineModeGame, () => {
    handlePreviewUnloadPositionOptions(engine)
    return EventResult.Stop
  })

  // command unit to annex
  mountInputActionListenerWhen(InputActionType.UnitAnnex, isEngineModeGame, () => {
    const draftMove = getDraftMove()
    if (draftMove) {
      const { unit } = draftMove
      if (draftMove.destPosition) {
        if (firstIfOnlyOne(draftMove.destPositionPaths?.lastStep?.annex)) {
          console.log('annex.draftMove', deepClone(draftMove))
          dispatchClient(engine, createMoveUnitAction({ ...draftMove!, annex: true }))
        }
      } else if (isEntityInfantry(unit)) {
        const annexableEntityList = getEntitiesAtPosition(engine.state.ents, unit).filter((ent) => canUnitAnnexEntity(unit, ent))
        if (annexableEntityList.length === 1) {
          dispatchClient(engine, createMoveUnitAction({ ...draftMove, annex: true }))
        }
      }
      return EventResult.Stop
    }
  })

  // preview bombard options
  mountInputActionListenerWhen(InputActionType.UnitBombard, isEngineModeGame, () => {
    handlePreviewRangedAttackOptions(engine)
    return EventResult.Stop
  })

  // command unit to merge
  mountInputActionListenerWhen(InputActionType.UnitMerge, isEngineModeGame, () => {
    const draftMove = getDraftMove()
    if (draftMove) {
      const merge = draftMove.destPositionPaths?.lastStep?.merge
      if (merge) {
        dispatchClient(engine, createMoveUnitAction({ ...draftMove, merge }))
      }
    }
    return EventResult.Stop
  })


  function onGamePlayKeyDown(event: KeyboardEvent) {
    // console.log('onGamePlayKeyDown', event.key, event.code, event)
    if (shouldIgnoreHotKeys(event)) {
      // console.log('shouldIgnoreHotKeys returned true, ignoring')
      return
    }

    // const isGamePlay = isEngineModeGame
    // const isMapEditor = isEngineMapEditor(engine)

    for (let i = 0; i < actionBindings.length; i++) {
      const { action, input } = actionBindings[i]
      const isMatch = keyboardEventMatchesInputActionBinding(event, input)
      if (event.code === 'KeyP' || event.code === 'Quote' || (event.code === 'KeyL' && event.shiftKey)) {
        // console.log('onGamePlayKeyDown.debug', { eventCode: event.code, eventShift: event.shiftKey, action, input, isMatch })
      }
      if (isMatch) {
        // console.log('onGamePlayKeyDown.match found', action, input)
        const result = emitEvent(getInputActionListenerList(action), event)
        if (result === EventResult.Stop) {
          // console.log('onGamePlayKeyDown.emitEvent.EventResult.Stop')
          return
        }
        // console.log('onGamePlayKeyDown.match.action.listeners.exhausted')
        // we'll continue to loop,
        // because another action might claim the event.
      }
    }
    // console.log('onGamePlayKeyDown.loopEnded')

    // if (key === 'Escape') {
    //   // console.log('[esc] pressed')
    //   preventDefault(event)
    //   emitEvent(keyDownEscapeELL, event)
    //   return
    // }

    // IDK how to implement grid in 3D
    // if (key === 'g' && event.ctrlKey) {
    //   console.log('[ctrl+g] pressed')
    //   preventDefault(event)
    //   // TODO recenter camera with respect to lost/gained pixels
    //   engine.viewCtx.tile_gap_px = engine.viewCtx.tile_gap_px === 0 ? 1 : 0
    //   return
    // }

    // if (key === 's') {
    //   if (event.metaKey || event.ctrlKey) {
    //     // console.log('Save Hotkey!')
    //     preventDefault(event)
    //     saveStateChanges(engine)
    //     dispatchClient(engine, createSetStateAction(engine.state))
    //     return
    //   }
    // }
    // const { draftMove } = engine
    // if (key === 'z') {
    //   if (event.metaKey || event.ctrlKey) {
    //     preventDefault(event)
    //     if (event.shiftKey) {
    //       // console.log('Redo Event!')
    //     } else {
    //       // console.log('Undo Event!')
    //     }
    //     return
    //   }
    // }

    // DoW:
    // j: jump or teleport
    // n: unload
    // y: set rally point
    // b: build menu (if builder selected)
    // shift: queue orders
    // .>: next builder
    // ctrl + R: next unit
    // u: upgrade
    // "Backspace": default camera angle
    // ': snap to unit (I'll say centerpan?)
    // CivV:
    // F1: civilopedia
    // F2: Economic
    // F3: Military
    // F4: Diplomacy
    // F5: Social Policies
    // F6: Research
    // F7: Notifications
    // F8: Victory Progress
    // F9: Demographics
    // F10: Strategic View (this could mean zoom out or see overworld map)
    // F11: Quick Save
    // F12: Quick Load
    // Shift+Enter: Next Turn
    // g : turn on/off grid
    // ctrl+R resource icons
    // ctrl+Y yield icons
    // +-: Zoom
    // PageUp/PageDown: Zoom
    // Insert/Home/End: Capital City View
    // Esc: Menu
    // Ctrl+O: Game Options
    // Space: skip unit
    // >: next unit
    // <: prev unit
    // m: move mode
    // e: explore
    // a: alert
    // f: sleep/fortify
    // Delete: disband
    // ctrl+a: attack
    // b: ranged attack
    // s: set up artillary
    // h: heal+fortify
    // BackSpace: cancel last mission
    // I: intercept air units
    //

    // console.log(`[${key}] pressed`, 'No key handler returned early', event)

    // if (key === 'Enter') {
    //   console.log('[enter] pressed')
    //   preventDefault(event)
    //   if (engine.draftMove) {
    //     dispatchClient(engine, createMoveUnitAction(engine.draftMove))
    //   }
    //   return
    // }
  }

  // function onGamePlayKeyUp() {
  //   // console.log('onGamePlayKeyUp', event)
  //   // if (key === 's') {
  //   //   if (event.metaKey || event.ctrlKey) {
  //   //     console.log('Save Hotkey!')
  //   //     preventDefault(event)
  //   //     saveStateChanges(engine)
  //   //     return
  //   //   }
  //   // }
  // }

  // onWindowEvent('keyup', onGamePlayKeyUp)
  onWindowEvent('keydown', onGamePlayKeyDown)
}

function shouldIgnoreHotKeys(event: KeyboardEvent): boolean {
  if (event.metaKey || event.ctrlKey) {
    return false
  }

  const target = event.target as HTMLElement | null
  const targetNodeName = target?.nodeName
  // console.log('targetNodeName', targetNodeName)
  return (targetNodeName === 'INPUT' ||
    targetNodeName === 'TEXTAREA' ||
    target?.isContentEditable) as boolean
}
